/**
 * Payment history status normalization for Credit Report UI.
 *
 * Only explicit on-time values become OK.
 * Blank / unknown / unavailable become "-".
 * Metro2 digits 1-9 map to late / charge-off badges.
 */

export const BUREAU_NAMES = ['TransUnion', 'Experian', 'Equifax'];

const STATUS_SEVERITY = {
  '-': 0,
  ND: 0,
  OK: 1,
  30: 2,
  60: 3,
  90: 4,
  120: 5,
  150: 6,
  180: 7,
  CO: 8,
  COLL: 8,
};

const OK_EXACT = new Set([
  'OK',
  'C',
  '0',
  '000',
  'CURRENT',
  'PAID',
  'PAYS AS AGREED',
  'AS AGREED',
]);

const MISSING_EXACT = new Set([
  '',
  '-',
  '—',
  'ND',
  'N/D',
  'NA',
  'N/A',
  'NONE',
  'NULL',
  'UNKNOWN',
  'UNAVAILABLE',
  'NOT REPORTED',
  'NO DATA',
  'X',
  '*',
  'U',
]);

const METRO2_DIGIT_MAP = {
  0: 'OK',
  1: '30',
  2: '60',
  3: '90',
  4: '120',
  5: '150',
  6: '180',
  7: '180',
  8: 'CO',
  9: 'CO',
};

export function normalizePaymentStatus(status) {
  if (status == null) return '-';

  if (typeof status === 'number' && Number.isFinite(status)) {
    const digit = String(Math.trunc(status));
    return METRO2_DIGIT_MAP[digit] || '-';
  }

  const raw = String(status).trim();
  if (!raw) return '-';

  const upper = raw.replace(/\s+/g, ' ').toUpperCase();
  const compact = upper.replace(/\s+/g, '');

  if (MISSING_EXACT.has(upper) || MISSING_EXACT.has(compact)) return '-';
  if (OK_EXACT.has(upper) || compact === 'PAYSASAGREED' || compact === 'ASAGREED') return 'OK';

  if (
    compact === 'CO'
    || compact === 'C/O'
    || compact === 'CHARGEOFF'
    || compact === 'CHARGEDOFF'
    || compact === 'CHARGE-OFF'
    || (upper.includes('CHARGE') && upper.includes('OFF'))
  ) {
    return 'CO';
  }

  if (upper.includes('COLLECTION') || compact === 'COLL' || compact === 'COL') {
    return 'CO';
  }

  // Account-level phrases like "Late 120 Days"
  const latePhrase = upper.match(/(?:late\s*)?(?:0*)(30|60|90|120|150|180)(?:\s*days?(?:\s*late)?)?/i)
    || compact.match(/(?:late)?(?:0*)(30|60|90|120|150|180)(?:days?(?:late)?)?/i);
  if (latePhrase && (upper.includes('LATE') || upper.includes('DAY'))) {
    return latePhrase[1];
  }

  const dayMatch = upper.match(/^(?:0*)(30|60|90|120|150|180)(?:\s*DAYS?(?:\s*LATE)?)?$/i)
    || compact.match(/^(?:0*)(30|60|90|120|150|180)(?:DAYS?(?:LATE)?)?$/i);
  if (dayMatch) return dayMatch[1];

  if (Object.prototype.hasOwnProperty.call(METRO2_DIGIT_MAP, compact)) {
    return METRO2_DIGIT_MAP[compact];
  }

  if (['30', '60', '90', '120', '150', '180'].includes(upper)) return upper;

  if (upper.includes('30') && upper.includes('DAY')) return '30';
  if (upper.includes('60') && upper.includes('DAY')) return '60';
  if (upper.includes('90') && upper.includes('DAY')) return '90';
  if (upper.includes('120') && upper.includes('DAY')) return '120';
  if (upper.includes('150') && upper.includes('DAY')) return '150';
  if (upper.includes('180') && upper.includes('DAY')) return '180';

  if (upper.includes('PAST DUE') || upper.includes('DELINQUENT')) return '30';

  if (['CL', 'CLOSED', 'NC', 'NOT CURRENT', 'BR'].includes(upper)) return '-';

  return '-';
}

export function paymentStatusSeverity(status) {
  return STATUS_SEVERITY[normalizePaymentStatus(status)] ?? 0;
}

export function isLatePaymentStatus(status) {
  const text = String(status || '').trim().toLowerCase();
  if (text && /\blate\b|\bpast\s+due\b|\bdelinquent\b/.test(text)) {
    if (!/^(paid|current|pays?\s+as\s+agreed|ok|c|0|000)$/i.test(text)) {
      const normalized = normalizePaymentStatus(status);
      if ((STATUS_SEVERITY[normalized] ?? 0) >= 2) return true;
      return true;
    }
  }
  return paymentStatusSeverity(status) >= 2;
}

export function isChargeOffStatus(status) {
  const text = String(status || '').trim().toLowerCase();
  if (text.includes('charge') && text.includes('off')) return true;
  return normalizePaymentStatus(status) === 'CO' && !text.includes('collection');
}

export function isMetro2ChargeOffRating(status) {
  const text = String(status || '').trim().toLowerCase();
  if (!text) return false;
  if (text.includes('charge') && text.includes('off')) return true;
  const compact = text.replace(/[\s_-]+/g, '');
  return compact.includes('chargeoff') || compact.includes('collectionorchargeoff');
}

const TRUE_COLLECTION_REMARK_MARKERS = [
  'placed for collection',
  'placed with collection',
  'transferred to collection',
  'transferred for collection',
  'assigned to collection',
  'assigned for collection',
  'debt buyer',
  'debt-buyer',
  'collection agency',
  'collection account',
];

/**
 * Join ALL status/rating-bearing fields. Never use `a || b` — a truthy
 * account status like "Closed" must not hide Metro2 text on payment_status.
 */
export function assembleStatusRatingText(tradeline = {}) {
  const fields = [
    tradeline.status,
    tradeline.account_status,
    tradeline.accountStatus,
    tradeline.payment_status,
    tradeline.paymentStatus,
    tradeline.current_rating,
    tradeline.currentRating,
    tradeline.highest_adverse_rating,
    tradeline.highestAdverseRating,
    tradeline.most_recent_adverse_rating,
    tradeline.mostRecentAdverseRating,
    tradeline.account_type_detail,
    tradeline.accountTypeDetail,
    tradeline.account_type,
    tradeline.accountType,
    tradeline.remarks,
    tradeline.remark,
    tradeline.comments,
    tradeline.comment,
    tradeline.special_comments,
    tradeline.specialComments,
  ];
  return fields
    .map((value) => String(value || '').trim())
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
}

function _isTrueCollectionAccountType(value) {
  const raw = String(value || '').trim().toLowerCase();
  if (!raw) return false;
  if (isMetro2ChargeOffRating(raw)) return false;
  const compact = raw.replace(/\s+/g, ' ').trim();
  if (compact === 'collection' || compact === 'collections' || compact === 'collection account') {
    return true;
  }
  if (compact.startsWith('collection account') || compact.includes('collection agency')) {
    return true;
  }
  return false;
}

/**
 * True recovery/collection evidence — not Metro2 rating text alone.
 */
export function hasTrueCollectionRecoveryEvidence(tradeline = {}) {
  if (
    tradeline.collection_agency
    || tradeline.collectionAgency
    || tradeline.collector_name
    || tradeline.collectorName
    || tradeline.collector
  ) {
    return true;
  }
  if (
    tradeline.is_debt_buyer === true
    || tradeline.isDebtBuyer === true
    || tradeline.is_repossession === true
    || tradeline.isRepossession === true
    || tradeline.is_surrender === true
    || tradeline.isSurrender === true
    || tradeline.is_transferred_post_default === true
    || tradeline.isTransferredPostDefault === true
    || tradeline.transferred_post_default === true
    || tradeline.is_foreclosure === true
    || tradeline.isForeclosure === true
  ) {
    return true;
  }
  if (
    _isTrueCollectionAccountType(tradeline.account_type)
    || _isTrueCollectionAccountType(tradeline.accountType)
    || _isTrueCollectionAccountType(tradeline.account_type_detail)
    || _isTrueCollectionAccountType(tradeline.accountTypeDetail)
  ) {
    return true;
  }

  const remarks = [
    tradeline.remarks,
    tradeline.remark,
    tradeline.comments,
    tradeline.comment,
    tradeline.special_comments,
    tradeline.specialComments,
  ]
    .map((value) => String(value || '').toLowerCase())
    .join(' ');
  if (TRUE_COLLECTION_REMARK_MARKERS.some((marker) => remarks.includes(marker))) {
    return true;
  }

  // Creditor / original-creditor debt-buyer style names only with agency/type context
  // are already covered above via flags/agency. Do not invent recovery from names alone
  // when the only status text is a Metro2 rating (KOALAFI).
  return false;
}

/**
 * Derive is_collection for a tradeline.
 * Metro2 "Collection Or Charge Off" (and variants) alone → not collection,
 * even when an incoming is_collection flag is polluted.
 * True recovery evidence still wins.
 */
export function deriveTradelineIsCollection(tradeline = {}) {
  if (hasTrueCollectionRecoveryEvidence(tradeline)) return true;

  const statusText = assembleStatusRatingText(tradeline);
  if (isMetro2ChargeOffRating(statusText)) return false;

  // Do not preserve polluted is_collection when the only collection-like text is Metro2.
  // (Metro2 already returned false above.) Bare flag without recovery evidence is kept
  // only when status text is not a Metro2 charge-off rating.
  if (tradeline.is_collection === true || tradeline.isCollection === true) return true;
  return isCollectionStatus(statusText);
}

/**
 * Derive is_charge_off from any status/rating field (not status alone).
 */
export function deriveTradelineIsChargeOff(tradeline = {}) {
  if (tradeline.is_charge_off === true || tradeline.isChargeOff === true || tradeline.is_chargeoff === true) {
    return true;
  }
  const statusText = assembleStatusRatingText(tradeline);
  return isMetro2ChargeOffRating(statusText) || isChargeOffStatus(statusText);
}

export function isCollectionStatus(status) {
  const text = String(status || '').trim().toLowerCase();
  if (!text) return false;
  // Metro2 rating phrases are charge-off ratings, not true collection recovery.
  if (isMetro2ChargeOffRating(text)) return false;
  return text.includes('collection') || text === 'coll' || text === 'col';
}

export function isNegativePaymentStatus(status) {
  if (status == null) return false;
  const text = String(status).trim().toLowerCase();
  if (!text) return false;
  if (/^(current|paid|pays?\s+as\s+agreed|paid\s+as\s+agreed|ok|c|0|000|never\s+late)$/i.test(text)) {
    return false;
  }
  if (isLatePaymentStatus(status) || isChargeOffStatus(status) || isCollectionStatus(status)) {
    return true;
  }
  return ['late', 'past due', 'delinquent', 'serious delinquency', 'charge off', 'charged off', 'collection']
    .some((token) => text.includes(token));
}

export function normalizePaymentHistoryCell(value) {
  return normalizePaymentStatus(value);
}

export function canonicalizeBureauName(bureau) {
  const text = String(bureau || '').trim().toLowerCase();
  if (!text) return null;
  if (text.includes('trans')) return 'TransUnion';
  if (text.includes('exper')) return 'Experian';
  if (text.includes('equif')) return 'Equifax';
  return null;
}

function lookupMonthStatus(yearData, monthNum) {
  if (!yearData || typeof yearData !== 'object') return undefined;
  const keys = [
    monthNum,
    String(monthNum),
    String(monthNum).padStart(2, '0'),
  ];
  for (const key of keys) {
    if (Object.prototype.hasOwnProperty.call(yearData, key)) {
      return yearData[key];
    }
  }
  return undefined;
}

function trackMonth(allMonths, year, month) {
  if (year && month) {
    allMonths.add(`${year}-${String(month).padStart(2, '0')}`);
  }
}

function pushEntry(history, bureau, year, month, status) {
  history[bureau].push({
    year: Number(year),
    month: Number(month),
    status: normalizePaymentStatus(status),
  });
  trackMonth(history.allMonths, Number(year), Number(month));
}

/**
 * Parse account payment history into bureau/month entries.
 * Missing statuses remain "-" — never defaulted to OK.
 * Never clones one bureau's history into another bureau.
 */
export function parsePaymentHistory(account = {}) {
  const history = {
    TransUnion: [],
    Experian: [],
    Equifax: [],
    allMonths: new Set(),
  };

  const grid =
    account.payment_history_grid
    || account.paymentHistoryGrid
    || account.bureauPaymentHistory
    || account.bureau_payment_history;

  if (grid && typeof grid === 'object') {
    BUREAU_NAMES.forEach((bureau) => {
      const bureauData = grid[bureau]
        || grid[bureau.toLowerCase()]
        || grid[canonicalizeBureauName(bureau)]
        || {};

      // Empty object / null means this bureau has no history — do not invent
      if (!bureauData || typeof bureauData !== 'object' || Array.isArray(bureauData)) {
        return;
      }
      if (Object.keys(bureauData).length === 0) {
        return;
      }

      Object.keys(bureauData).sort().reverse().forEach((year) => {
        const yearData = bureauData[year];
        if (!yearData || typeof yearData !== 'object' || Array.isArray(yearData)) return;

        const monthKeys = Object.keys(yearData)
          .map((key) => parseInt(key, 10))
          .filter((month) => month >= 1 && month <= 12)
          .sort((a, b) => b - a);

        monthKeys.forEach((month) => {
          const raw = lookupMonthStatus(yearData, month);
          const status = raw === undefined ? '-' : raw;
          pushEntry(history, bureau, year, month, status);
        });
      });
    });
  }

  const paymentHistory = account.payment_history || account.paymentHistory;
  if (Array.isArray(paymentHistory)) {
    paymentHistory.forEach((entry) => {
      const bureau = canonicalizeBureauName(entry.bureau) || entry.bureau;
      if (!BUREAU_NAMES.includes(bureau)) return;
      pushEntry(
        history,
        bureau,
        entry.year,
        entry.month,
        entry.status ?? entry.payment_status ?? entry.normalized_status ?? entry.raw_status ?? '-',
      );
    });
  } else if (paymentHistory && typeof paymentHistory === 'object') {
    Object.entries(paymentHistory).forEach(([bureauKey, entries]) => {
      const bureau = canonicalizeBureauName(bureauKey);
      if (!bureau || !Array.isArray(entries)) return;
      entries.forEach((entry) => {
        if (entry && typeof entry === 'object') {
          pushEntry(
            history,
            bureau,
            entry.year,
            entry.month,
            entry.status ?? entry.payment_status ?? '-',
          );
        }
      });
    });
  }

  BUREAU_NAMES.forEach((bureau) => {
    const key = `payment_history_${bureau.toLowerCase()}`;
    const altKey = `${bureau.toLowerCase()}_payment_history`;
    const data = account[key] || account[altKey];
    if (!Array.isArray(data)) return;

    data.forEach((item) => {
      if (item && typeof item === 'object') {
        const year = item.year;
        const month = item.month;
        if (year && month) {
          pushEntry(history, bureau, year, month, item.status ?? item.payment_status ?? '-');
        }
      }
    });
  });

  return history;
}

export function generateMonthColumnsFromData(allMonths, limit = null) {
  const sortedMonths = Array.from(allMonths).sort().reverse();
  const monthsToShow = limit ? sortedMonths.slice(0, limit) : sortedMonths;
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  return monthsToShow.map((monthKey) => {
    const [year, month] = monthKey.split('-').map(Number);
    return {
      year,
      month,
      label: `${monthNames[month - 1] || ''} ${String(year).slice(2)}`,
    };
  });
}

export function getStatusForBureauMonth(paymentHistory, bureau, year, month) {
  const entries = paymentHistory?.[bureau] || [];
  const entry = entries.find((item) => item.year === year && item.month === month);
  return normalizePaymentStatus(entry?.status ?? '-');
}
